import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchUsers, createUser, updateUser, deleteUser, sendActivationEmail, sendResetPasswordEmail, updateUserManager } from "@/features/user/services/userService";
const useUsers = (page, filters, all = false, search, limit = 10) => {
    const queryClient = useQueryClient();
    // Use a dynamic query key based on the "all" flag.
    const queryKey = all
        ? ["users", "all", search, limit]
        : ["users", { page, filters, search, limit }];
    // Fetch users using the unified service function.
    const searchFilters = search
        ? { ...filters, first_name: search, last_name: search, email: search }
        : filters;
    const { data, error, isLoading, refetch } = useQuery(queryKey, () => fetchUsers(page, searchFilters, all, limit), {
        keepPreviousData: true,
        staleTime: 60000,
    });
    // Mutation for creating a user.
    const createUserMutation = useMutation((newUser) => createUser(newUser), {
        onSuccess: (data) => {
            queryClient.invalidateQueries("users");
            const newUserId = data?.data?.user?.id;
            if (newUserId) {
                sendActivationEmail(newUserId).catch(() => { });
            }
        },
    });
    // Mutation for updating a user.
    const updateUserMutation = useMutation(({ userId, userData }) => updateUser(userId, userData), {
        onSuccess: () => {
            queryClient.invalidateQueries("users");
        },
    });
    const updateManagerMutation = useMutation(({ userId, managerId }) => updateUserManager(userId, managerId), {
        onSuccess: () => {
            queryClient.invalidateQueries("users");
        },
    });
    // Mutation for deleting a user.
    const deleteUserMutation = useMutation((userId) => deleteUser(userId), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
        },
    });
    // Mutation for sending a reset password email.
    const resetPasswordMutation = useMutation((userId) => sendResetPasswordEmail(userId));
    return {
        users: data?.users || [],
        meta: data?.meta || null,
        loading: isLoading,
        error: error ? error.message : null,
        refetchUsers: refetch,
        handleCreateUser: createUserMutation.mutateAsync,
        handleUpdateUser: (userId, userData) => updateUserMutation.mutateAsync({ userId, userData }),
        handleDeleteUser: deleteUserMutation.mutateAsync,
        handleResetPassword: resetPasswordMutation.mutateAsync,
        handleUpdateManager: (userId, managerId) => updateManagerMutation.mutateAsync({ userId, managerId }),
        isUpdatingManager: updateManagerMutation.isLoading,
    };
};
export default useUsers;
